import { ImageMetadata, ImageMetadataV1, ImageMetadataV2 } from '../dto/EntityRepository';
import { getImageDeviceType } from './deviceUtils';

// 타입 가드 함수들
const isMetadataV1 = (metadata: ImageMetadata): metadata is ImageMetadataV1 => {
  return 'MOBILE' in metadata && typeof metadata.MOBILE === 'boolean';
};

const isMetadataV2 = (metadata: ImageMetadata): metadata is ImageMetadataV2 => {
  return 'available' in metadata && 'origin_ext' in metadata;
};

// 두 구조에서 디바이스 타입의 가용성을 확인하는 헬퍼 함수
export const isDeviceAvailable = (metadata: ImageMetadata, deviceType: string): boolean => {
  if (isMetadataV1(metadata)) {
    return metadata[deviceType as keyof ImageMetadataV1] === true;
  } else if (isMetadataV2(metadata)) {
    return metadata.available[deviceType as keyof typeof metadata.available] === true;
  }
  return false;
};

// 파일 확장자 관련 헬퍼 함수들
const getOriginExt = (metadata: ImageMetadata): string => {
  if (isMetadataV2(metadata)) {
    return metadata.origin_ext;
  }
  return ''; // V1에서는 확장자를 바꾸지 않음
};

const getResizedExt = (metadata: ImageMetadata): string => {
  if (isMetadataV2(metadata)) {
    return metadata.resized_ext;
  }
  return ''; // V1에서는 확장자를 바꾸지 않음
};

/** 마지막 경로 세그먼트의 이미지 파일명(쿼리·해시 앞까지)만 치환 */
const IMAGE_FILE_IN_PATH_RE = /\/([^/?#]+)\.(jpeg|jpg|png|gif|webp)((?:\?[^#]*)?(?:#.*)?)$/i;

// URL 생성 헬퍼 함수
export const generateOptimizedUrl = (imageUrl: string, browserType: string, metadata: ImageMetadata): string => {
  const originExt = getOriginExt(metadata);
  const resizedExt = getResizedExt(metadata);

  // V1의 경우 확장자를 바꾸지 않음 (빈 문자열)
  if (originExt === '' && resizedExt === '') {
    // V1: 확장자 변경 없이 디바이스 타입만 추가
    return imageUrl.replace(IMAGE_FILE_IN_PATH_RE, `/${browserType}.$2$3`);
  }
  // V2: 디바이스 타입과 확장자 모두 변경
  const extPart = browserType === 'origin' ? originExt : resizedExt;
  return imageUrl.replace(IMAGE_FILE_IN_PATH_RE, `/${browserType}${extPart}$3`);
};

const getNextDeviceType = (currentType: string, metadata: ImageMetadata): string => {
  const deviceOrder = ['MOBILE', 'TABLET', 'LAPTOP', 'DESKTOP', 'origin'];
  const currentIndex = deviceOrder.indexOf(currentType);

  for (let i = currentIndex + 1; i < deviceOrder.length; i++) {
    const nextType = deviceOrder[i];
    if (isDeviceAvailable(metadata, nextType)) {
      return nextType;
    }
  }

  return currentType;
};

/** mobile ~ desktop만 선택, origin 미포함 */
export const getFallbackDeviceType = (metadata: ImageMetadata, width: number): string => {
  const deviceType = getImageDeviceType(width, false);
  if (isDeviceAvailable(metadata, deviceType)) {
    return deviceType;
  } else {
    return getNextDeviceType(deviceType, metadata);
  }
};

export const isBase64 = (url: string): boolean => {
  const base64Pattern = /^data:image\/(jpeg|jpg|png|gif|webp);base64,/;
  return base64Pattern.test(url);
};

export const convertToJsonMetadata = (data: any): ImageMetadata => {
  // 새로운 구조인지 확인
  if (data.origin_ext && data.resized_ext && data.available) {
    return {
      origin_ext: data.origin_ext,
      resized_ext: data.resized_ext,
      available: {
        THUMBNAIL: data.available.THUMBNAIL === true,
        MOBILE: data.available.MOBILE === true,
        TABLET: data.available.TABLET === true,
        LAPTOP: data.available.LAPTOP === true,
        DESKTOP: data.available.DESKTOP === true,
        /** API가 `origin` 키를 생략해도 원본 파일은 존재하는 경우가 많아, 명시적 false만 비활성으로 봄 */
        origin: data.available.origin !== false,
      }
    };
  }
  
  // 이전 구조인 경우
  return {
    MOBILE: data.MOBILE === true,
    TABLET: data.TABLET === true,
    LAPTOP: data.LAPTOP === true,
    DESKTOP: data.DESKTOP === true,
    origin: true,
  };
}